import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createPreliminaryInvoiceApi } from '../features/invoice/invoice.service';
import '../scss/save-invoice-modal.scss';

interface SaveInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  getPayload?: () => any;
  onSecondApiCall?: (pdfBlob: Blob) => Promise<void>;
}

export const SaveInvoiceModal: React.FC<SaveInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  getPayload,
  onSecondApiCall
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      // Get payload from parent component
      if (!getPayload) {
        toast.error('Unable to process invoice. Please try again.');
        console.error('No getPayload function provided');
        setIsLoading(false);
        return;
      }

      const payload = await getPayload();
      
      if (!payload) {
        toast.error('Please fill in all required invoice details');
        console.error('Payload is null or undefined');
        setIsLoading(false);
        return;
      }

      // Step 1: Call preliminary invoice API to get PDF blob
      const pdfBlob = await createPreliminaryInvoiceApi(payload);
       
      // Step 2: Call second API with PDF blob (if provided)
      if (onSecondApiCall) {
        await onSecondApiCall(pdfBlob);
        }
      
      // Call original onSave if provided
      if (onSave) {
        onSave();
      }
      
      setIsLoading(false);
      onClose();
      
      // Bypass unsaved-changes guard since draft was just saved
      window.dispatchEvent(new CustomEvent('invoice-sent-successfully'));
      // Redirect to draft invoice page
      const currentLang = i18n.language || 'en-us';
      toast.success('Draft invoice saved successfully!');
      navigate(`/v2/draft-invoice?lang=${currentLang}`);
      
    } catch (error: any) { 
      console.error('❌ Error saving draft invoice:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save draft invoice';
      toast.error(errorMessage);
      setIsLoading(false);
      // Don't close modal on error so user can try again
    }
  };

  return (
    <div 
      className="save-invoice-modal-overlay"
      style={{ 
        WebkitTapHighlightColor: 'transparent',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none'
      } as React.CSSProperties}
    >
      <div 
        className="save-invoice-modal" 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          WebkitTapHighlightColor: 'transparent',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none'
        } as React.CSSProperties}
      >
        <div className="modal-header">
          <h3 className="modal-title">{t('SAVE_INVOICE') || 'Save Invoice'}</h3>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none'
            } as React.CSSProperties}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="#717680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="modal-body">
          <p>{t('ARE_YOU_SURE_YOU_WANT_TO_SAVE') || 'Are you sure you want to save?'}</p>
        </div>
        
        <div className="modal-footer">
          <button 
            className="modal-btn cancel-btn" 
            onClick={onClose} 
            disabled={isLoading}
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none'
            } as React.CSSProperties}
          >
            {t('CANCEL') || 'Cancel'}
          </button>
          <button 
            className="modal-btn save-btn" 
            onClick={handleSave} 
            disabled={isLoading}
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none'
            } as React.CSSProperties}
          >
            {isLoading ? (
              <>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }}>
                  <circle cx="7" cy="7" r="5" stroke="white" strokeWidth="2" strokeDasharray="20" strokeLinecap="round" opacity="0.3"/>
                  <circle cx="7" cy="7" r="5" stroke="white" strokeWidth="2" strokeDasharray="8 16" strokeLinecap="round"/>
                </svg>
                {t('SAVING') || 'Saving...'}
              </>
            ) : (
              <>{t('SAVE') || 'Save'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveInvoiceModal;
